import api from "@/app/_module/services/api";
import { getApiUrl } from "@/app/_module/services/config";
import { endpoint } from "@/app/_module/services/endpoints";
import { shouldUseDryRun } from "@/app/_module/utils/environment-config";
import { handleApiCall } from "@/app/_module/utils/apiResponseHandler";

import type { GetAnnualPremiumResponse } from "../types/annual-premium";
import type {
  GetBestMonthPremiumResponse,
  GetMonthlyPremiumResponse,
} from "../types/monthly-premium";
import type {
  DashboardOverviewResponse,
} from "../types/overview";
import type { GetBestWeekPremiumResponse } from "../types/weekly-premium";

export const getAnnualPremiumSales = async () => {
  try {
    // Don't pass agencyId as query param - backend gets it from x-agency-id header
    const url = getApiUrl(endpoint.sales.getPremiumAnnual);

    const result = await handleApiCall<GetAnnualPremiumResponse>(async () => {
      const response = await api.get<GetAnnualPremiumResponse>({
        url,
        auth: true,
      });
      return response;
    });

    // handleApiCall already extracted .data, so result is the actual data
    return result;
  } catch (error) {

    throw error;
  }
};

export const getMonthlyPremiumSales = async () => {
  try {
    // Ask for LAST month explicitly.
    //
    // The card is titled "Last Month Premium Sold" but this sent no params, and
    // the endpoint defaults to `now.getMonth()` — the CURRENT month. So in July
    // it showed July's sales under a "last month" heading. The endpoint keeping
    // "current month" as its default is reasonable for a generic API; the card
    // just has to say what it actually wants.
    //
    // Date(y, m - 1, 1) handles the January rollover on its own: month -1 in
    // January yields December of the previous year.
    const now = new Date();
    const lastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    // Backend expects a 1-based month (it does `parseInt(month) - 1`).
    const params = `?year=${lastMonth.getFullYear()}&month=${lastMonth.getMonth() + 1}`;

    // Don't pass agencyId as query param - backend gets it from x-agency-id header
    const url = getApiUrl(endpoint.sales.getPremiumMonthly) + params;

    const result = await handleApiCall<GetMonthlyPremiumResponse>(async () => {
      const response = await api.get<GetMonthlyPremiumResponse>({
        url,
        auth: true,
      });
      return response;
    });
    // handleApiCall already extracted .data, so result is the actual data
    return result;
  } catch (error) {

    throw error;
  }
};

export const getBestMonthsPremium = async () => {
  try {
    // Don't pass agencyId as query param - backend gets it from x-agency-id header
    const url = getApiUrl(endpoint.sales.getBestPremiumMonthly) + "?limit=12";

    const result = await handleApiCall<GetBestMonthPremiumResponse>(async () => {
      const response = await api.get<GetBestMonthPremiumResponse>({
        url,
        auth: true,
      });
      return response;
    });
    // handleApiCall already extracted .data, so result is the actual data
    return result;
  } catch (error) {

    throw error;
  }
};

export const getBestWeeksPremium = async () => {
  try {
    // Don't pass agencyId as query param - backend gets it from x-agency-id header
    const url = getApiUrl(endpoint.sales.getBestPremiumWeekly) + "?limit=6";

    const result = await handleApiCall<GetBestWeekPremiumResponse>(async () => {
      const response = await api.get<GetBestWeekPremiumResponse>({
        url,
        auth: true,
      });
      return response;
    });
    // handleApiCall already extracted .data, so result is the actual data
    return result;
  } catch (error) {

    throw error;
  }
};

export const getDashboardOverview = async (period?: string, startDate?: string, endDate?: string): Promise<DashboardOverviewResponse> => {
  try {
    // Don't pass agencyId as query param - backend gets it from x-agency-id header
    const path = endpoint.dashboard.getOverviewWithFilter(undefined, period, startDate, endDate);
    const url = getApiUrl(path);

    const result = await handleApiCall<DashboardOverviewResponse>(async () => {
      const response = await api.get<DashboardOverviewResponse>({
        url,
        auth: true,
      });
      return response;
    });

    // Validate result structure
    if (!result || typeof result !== 'object') {
      throw new Error('Dashboard overview data is invalid');
    }

    return result;
  } catch (error) {

    throw error;
  }
};